import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  type Edge,
  Handle,
  MarkerType,
  type Node,
  type NodeChange,
  type NodeTypes,
  Panel,
  Position,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import { Bot, Clock, GitBranch, Plus, Save, Trash2, X } from "lucide-react";
import { Toggle } from "@/components/shared/Toggle";
import { agentService } from "@/lib/services";
import type { Agent } from "@/types";

// ============================================================================
// Types
// ============================================================================

interface FlowBuilderStep {
  agent_uuid?: string;
  step_type?: string;
  instructions?: string;
  expected_output?: string;
  config?: Record<string, unknown>;
  requires_review?: boolean;
}

interface FlowBuilderConnector {
  from_step_order?: number;
  to_step_order: number;
  label?: string;
}

interface FlowBuilderProps {
  initialSteps?: Array<{
    id?: number;
    agentUuid?: string;
    agentName?: string;
    config?: Record<string, unknown>;
    requiresReview?: boolean;
  }>;
  initialConnectors?: Array<{
    fromStepId?: number;
    toStepId: number;
  }>;
  onSave: (steps: FlowBuilderStep[], connectors?: FlowBuilderConnector[]) => void;
  onCancel: () => void;
}

interface StepNodeData {
  agentUuid?: string;
  agentName?: string;
  requiresReview: boolean;
  config?: Record<string, unknown>;
  label: string;
  onDelete?: (nodeId: string) => void;
  onAddChild?: (nodeId: string) => void;
  onInsertBefore?: (nodeId: string) => void;
}

// ============================================================================
// Layout
// ============================================================================

function AgentPickerList({ agents, onAssign }: Readonly<{ agents: Agent[]; onAssign: (agent: Agent) => void }>) {
  if (agents.length === 0) {
    return <div className="text-center py-8 text-content-tertiary">No agents available.</div>;
  }
  return (
    <div className="space-y-2">
      {agents.map((agent) => (
        <button
          key={agent.uuid}
          onClick={() => onAssign(agent)}
          className="w-full text-left p-3 bg-surface-secondary hover:bg-interactive-active border border-border-secondary rounded-lg transition-colors"
        >
          <div className="font-medium text-content-primary text-sm">{agent.name}</div>
          {agent.description && (
            <div className="text-xs text-content-tertiary mt-1 line-clamp-2">{agent.description}</div>
          )}
        </button>
      ))}
    </div>
  );
}

const NODE_X = 400;
const NODE_H_GAP = 300;
const NODE_V_GAP = 160;
const NODE_Y_START = 60;

let nodeIdCounter = 0;
function generateNodeId() {
  return `step-${Date.now()}-${nodeIdCounter++}`;
}

function buildParentMapFromConnectors(
  initNodes: Node<StepNodeData>[],
  initialConnectors: NonNullable<FlowBuilderProps["initialConnectors"]>,
  dbIdToNodeId: Map<number, string>,
): Map<string, string | null> {
  const pm = new Map<string, string | null>();
  if (initialConnectors.length > 0) {
    for (const node of initNodes) {
      pm.set(node.id, null);
    }
    for (const conn of initialConnectors) {
      const toNodeId = dbIdToNodeId.get(conn.toStepId);
      const fromNodeId = conn.fromStepId != null ? dbIdToNodeId.get(conn.fromStepId) : null;
      if (toNodeId) {
        pm.set(toNodeId, fromNodeId ?? null);
      }
    }
  } else {
    let prevId: string | null = null;
    for (const node of initNodes) {
      pm.set(node.id, prevId);
      prevId = node.id;
    }
  }
  return pm;
}

function buildInitialNodesAndParentMap(
  initialSteps: NonNullable<FlowBuilderProps["initialSteps"]>,
  initialConnectors: NonNullable<FlowBuilderProps["initialConnectors"]>,
  handlers: {
    onDelete: (nodeId: string) => void;
    onAddChild: (nodeId: string) => void;
    onInsertBefore: (nodeId: string) => void;
  },
): { nodes: Node<StepNodeData>[]; parentMap: Map<string, string | null> } {
  const initNodes: Node<StepNodeData>[] = [];
  const dbIdToNodeId = new Map<number, string>();

  for (let i = 0; i < initialSteps.length; i++) {
    const step = initialSteps[i];
    const id = generateNodeId();
    if (step.id != null) dbIdToNodeId.set(step.id, id);
    initNodes.push({
      id,
      type: "step",
      position: { x: NODE_X, y: 0 },
      data: {
        agentUuid: step.agentUuid,
        agentName: step.agentName,
        requiresReview: step.requiresReview || false,
        config: step.config,
        label: (step.config?.label as string) || step.agentName || `Step ${i + 1}`,
        onDelete: handlers.onDelete,
        onAddChild: handlers.onAddChild,
        onInsertBefore: handlers.onInsertBefore,
      },
    });
  }

  const parentMap = buildParentMapFromConnectors(initNodes, initialConnectors, dbIdToNodeId);
  return { nodes: initNodes, parentMap };
}

function computeTreeLayout(
  nodes: Node<StepNodeData>[],
  parentMap: Map<string, string | null>,
): Map<string, { x: number; y: number }> {
  const childrenOf = new Map<string | null, string[]>();
  for (const node of nodes) {
    const pid = parentMap.get(node.id) ?? null;
    if (!childrenOf.has(pid)) childrenOf.set(pid, []);
    childrenOf.get(pid)!.push(node.id);
  }

  const positions = new Map<string, { x: number; y: number }>();
  const subtreeWidth = new Map<string, number>();

  function getWidth(nodeId: string): number {
    const children = childrenOf.get(nodeId) || [];
    if (children.length === 0) {
      subtreeWidth.set(nodeId, 1);
      return 1;
    }
    const total = children.reduce((sum, cid) => sum + getWidth(cid), 0);
    subtreeWidth.set(nodeId, total);
    return total;
  }

  function assignPositions(nodeId: string, centerX: number, depth: number) {
    positions.set(nodeId, { x: centerX, y: NODE_Y_START + depth * NODE_V_GAP });
    const children = childrenOf.get(nodeId) || [];
    if (children.length === 0) return;

    const totalChildWidth = children.reduce((s, cid) => s + (subtreeWidth.get(cid) || 1), 0);
    let offsetX = centerX - ((totalChildWidth - 1) * NODE_H_GAP) / 2;

    for (const childId of children) {
      const w = subtreeWidth.get(childId) || 1;
      assignPositions(childId, offsetX + ((w - 1) * NODE_H_GAP) / 2, depth + 1);
      offsetX += w * NODE_H_GAP;
    }
  }

  const roots = childrenOf.get(null) || [];
  for (const rid of roots) getWidth(rid);

  // Single tree: center at NODE_X. Multiple roots: spread them.
  if (roots.length === 1) {
    assignPositions(roots[0], NODE_X, 0);
  } else {
    let x = NODE_X;
    for (const rid of roots) {
      const w = subtreeWidth.get(rid) || 1;
      assignPositions(rid, x, 0);
      x += w * NODE_H_GAP + NODE_H_GAP;
    }
  }

  return positions;
}

function buildTreeEdges(nodes: Node<StepNodeData>[], parentMap: Map<string, string | null>): Edge[] {
  const edges: Edge[] = [];
  for (const node of nodes) {
    const pid = parentMap.get(node.id);
    if (pid) {
      edges.push({
        id: `e-${pid}-${node.id}`,
        source: pid,
        target: node.id,
        type: "smoothstep",
        animated: true,
        style: { stroke: "#EC622B", strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: "#EC622B", width: 20, height: 20 },
      });
    }
  }
  return edges;
}

// ============================================================================
// Custom Node
// ============================================================================

function StepNode({ id, data }: Readonly<{ id: string; data: StepNodeData }>) {
  return (
    <div className="relative">
      <Handle type="target" position={Position.Top} className="bg-brand-primary! border-brand-primary! w-3! h-3!" />

      {/* Insert before (above this step) */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          data.onInsertBefore?.(id);
        }}
        className="absolute -top-3 left-1/2 -translate-x-1/2 h-6 w-6 rounded-full bg-surface-tertiary text-content-tertiary flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-brand-primary hover:text-white shadow-md z-10"
        title="Insert step before"
      >
        <Plus className="h-3 w-3" />
      </button>

      <div className="bg-surface-secondary border-2 border-brand-primary rounded-lg p-4 min-w-[220px] max-w-[280px] shadow-lg group">
        {/* Delete */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            data.onDelete?.(id);
          }}
          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 shadow-md"
          title="Remove step and its children"
        >
          <Trash2 className="h-3 w-3" />
        </button>

        {/* Add child (fan-out) */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            data.onAddChild?.(id);
          }}
          className="absolute -bottom-3 left-1/2 -translate-x-1/2 h-6 w-6 rounded-full bg-brand-primary text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-brand-primary/80 shadow-md z-10"
          title="Add child step (fan-out)"
        >
          <Plus className="h-3 w-3" />
        </button>

        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-linear-to-br from-brand-primary/20 to-brand-secondary/20 flex items-center justify-center shrink-0">
            <Bot className="h-5 w-5 text-brand-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-content-primary text-sm truncate">{data.label || "New Step"}</div>
            {data.agentName && <div className="text-xs text-content-tertiary truncate">{data.agentName}</div>}
          </div>
          {data.requiresReview && (
            <div
              className="flex items-center gap-1 px-1.5 py-0.5 bg-yellow-500/10 border border-yellow-500/30 rounded-full"
              title="Requires human review"
            >
              <Clock className="h-3 w-3 text-yellow-400" />
            </div>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} className="bg-brand-primary! border-brand-primary! w-3! h-3!" />
    </div>
  );
}

const nodeTypes: NodeTypes = {
  step: StepNode as unknown as NodeTypes["default"],
};

// ============================================================================
// Main Component
// ============================================================================

export function FlowBuilder(props: Readonly<FlowBuilderProps>) {
  return (
    <ReactFlowProvider>
      <FlowBuilderInner {...props} />
    </ReactFlowProvider>
  );
}

function FlowBuilderInner({ initialSteps = [], initialConnectors = [], onSave, onCancel }: Readonly<FlowBuilderProps>) {
  const { fitView } = useReactFlow();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [showAgentPicker, setShowAgentPicker] = useState(false);
  const [selectedNode, setSelectedNode] = useState<Node<StepNodeData> | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState<StepNodeData>([]);
  const [edges, setEdges] = useEdgesState([]);
  const initialized = useRef(false);

  // parentMap: nodeId -> parentNodeId (null = root)
  const [parentMap, setParentMap] = useState<Map<string, string | null>>(new Map());

  useEffect(() => {
    loadAgents();
  }, []);

  // ---- helpers ----

  const findLastLeaf = useCallback((): string | null => {
    if (nodes.length === 0) return null;
    // Walk from the first root down to the deepest leaf (rightmost path)
    const childrenOf = new Map<string | null, string[]>();
    for (const [nid, pid] of parentMap.entries()) {
      if (!childrenOf.has(pid)) childrenOf.set(pid, []);
      childrenOf.get(pid)!.push(nid);
    }
    const roots = childrenOf.get(null) || [];
    if (roots.length === 0) return nodes.at(-1)?.id ?? null;
    let current = roots.at(-1)!;
    while (true) {
      const children = childrenOf.get(current) || [];
      if (children.length === 0) return current;
      current = children.at(-1)!;
    }
  }, [nodes, parentMap]);

  const addStep = useCallback(
    (parentId: string | null, label?: string) => {
      const newId = generateNodeId();
      setParentMap((prev) => {
        const next = new Map(prev);
        next.set(newId, parentId);
        return next;
      });
      setNodes((nds) => [
        ...nds,
        {
          id: newId,
          type: "step",
          position: { x: NODE_X, y: 0 },
          data: {
            requiresReview: false,
            config: {},
            label: label || `Step ${nds.length + 1}`,
            onDelete: handleDeleteNode,
            onAddChild: handleAddChild,
            onInsertBefore: handleInsertBefore,
          },
        },
      ]);
      return newId;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleDeleteNode = useCallback(
    (nodeId: string) => {
      setParentMap((prev) => {
        const next = new Map(prev);
        const toRemove = new Set<string>();
        const queue = [nodeId];
        while (queue.length > 0) {
          const cur = queue.pop()!;
          toRemove.add(cur);
          for (const [cid, pid] of next.entries()) {
            if (pid === cur) queue.push(cid);
          }
        }
        for (const id of toRemove) next.delete(id);
        return next;
      });
      setNodes((nds) => {
        // Collect all descendants
        const toRemove = new Set<string>();
        const queue = [nodeId];
        // Use current parentMap snapshot from nds context
        while (queue.length > 0) {
          const cur = queue.pop()!;
          toRemove.add(cur);
        }
        return nds.filter((n) => !toRemove.has(n.id));
      });
      setSelectedNode((prev) => (prev?.id === nodeId ? null : prev));
    },
    [setNodes],
  );

  const handleAddChild = useCallback(
    (parentNodeId: string) => {
      addStep(parentNodeId);
    },
    [addStep],
  );

  const handleInsertBefore = useCallback(
    (nodeId: string) => {
      // Insert a new step above the given node:
      // 1. New step takes the target node's parent
      // 2. Target node becomes child of the new step
      const newId = generateNodeId();
      setParentMap((prev) => {
        const next = new Map(prev);
        const currentParent = next.get(nodeId) ?? null;
        next.set(newId, currentParent); // new step gets target's old parent
        next.set(nodeId, newId); // target becomes child of new step
        return next;
      });
      setNodes((nds) => [
        ...nds,
        {
          id: newId,
          type: "step",
          position: { x: NODE_X, y: 0 },
          data: {
            requiresReview: false,
            config: {},
            label: `Step ${nds.length + 1}`,
            onDelete: handleDeleteNode,
            onAddChild: handleAddChild,
            onInsertBefore: handleInsertBefore,
          },
        },
      ]);
    },
    [setNodes, handleDeleteNode, handleAddChild],
  );

  // Re-bind callbacks on addStep — we need stable refs in node data
  // This is handled by the layout effect below.

  // ---- initialise from props ----

  useEffect(() => {
    if (initialSteps.length > 0 && !initialized.current) {
      initialized.current = true;

      const { nodes: initNodes, parentMap: pm } = buildInitialNodesAndParentMap(initialSteps, initialConnectors, {
        onDelete: handleDeleteNode,
        onAddChild: handleAddChild,
        onInsertBefore: handleInsertBefore,
      });

      setParentMap(pm);
      setNodes(initNodes);
    }
  }, [initialSteps, initialConnectors, handleDeleteNode, handleAddChild, handleInsertBefore, setNodes]);

  // ---- layout & edges ----

  useEffect(() => {
    if (nodes.length === 0) {
      setEdges([]);
      return;
    }

    const positions = computeTreeLayout(nodes, parentMap);
    const newEdges = buildTreeEdges(nodes, parentMap);

    setNodes((nds) =>
      nds.map((n) => {
        const pos = positions.get(n.id);
        return {
          ...n,
          position: pos ?? n.position,
          data: {
            ...n.data,
            onDelete: handleDeleteNode,
            onAddChild: handleAddChild,
            onInsertBefore: handleInsertBefore,
          },
        };
      }),
    );
    setEdges(newEdges);

    // Fit viewport to show all nodes after layout
    setTimeout(() => fitView({ padding: 0.2, duration: 200 }), 50);
  }, [parentMap, nodes.length, handleDeleteNode, handleAddChild, handleInsertBefore, setEdges, setNodes, fitView]);

  // ---- agents ----

  const loadAgents = async () => {
    try {
      setLoadingAgents(true);
      const response = await agentService.list();
      setAgents(response.data);
    } catch (err) {
      console.error("Error loading agents:", err);
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleAssignAgent = (agent: Agent) => {
    if (!selectedNode) return;
    handleUpdateNodeConfig({
      agentUuid: agent.uuid,
      agentName: agent.name,
      label:
        selectedNode.data.label === `Step ${nodes.indexOf(nodes.find((n) => n.id === selectedNode.id)!) + 1}` ||
        !selectedNode.data.label
          ? agent.name
          : selectedNode.data.label,
    });
    setShowAgentPicker(false);
  };

  const handleClearAgent = () => {
    if (!selectedNode) return;
    handleUpdateNodeConfig({
      agentUuid: undefined,
      agentName: undefined,
    });
  };

  // ---- interaction ----

  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      const filtered = changes.filter((c) => c.type !== "position");
      if (filtered.length > 0) onNodesChange(filtered);
    },
    [onNodesChange],
  );

  const handleNodeClick = (_event: React.MouseEvent, node: Node) => {
    setSelectedNode(node as Node<StepNodeData>);
    setShowAgentPicker(false);
  };

  const handleUpdateNodeConfig = (config: Partial<StepNodeData>) => {
    if (!selectedNode) return;
    setNodes((nds) => nds.map((n) => (n.id === selectedNode.id ? { ...n, data: { ...n.data, ...config } } : n)));
    setSelectedNode((prev) => (prev ? { ...prev, data: { ...prev.data, ...config } } : null));
  };

  // ---- "Add Step" button: chain to last leaf ----

  const handleAddStep = () => {
    const lastLeaf = findLastLeaf();
    addStep(lastLeaf);
  };

  // ---- save ----

  const handleSave = () => {
    const childrenOf = new Map<string | null, string[]>();
    for (const [nid, pid] of parentMap.entries()) {
      if (!childrenOf.has(pid)) childrenOf.set(pid, []);
      childrenOf.get(pid)!.push(nid);
    }

    const nodeById = new Map(nodes.map((n) => [n.id, n]));
    const orderedSteps: FlowBuilderStep[] = [];
    const connectors: FlowBuilderConnector[] = [];
    const orderMap = new Map<string, number>();

    const queue: string[] = [...(childrenOf.get(null) || [])];
    while (queue.length > 0) {
      const nid = queue.shift()!;
      const node = nodeById.get(nid);
      if (!node) continue;

      const stepOrder = orderedSteps.length;
      orderMap.set(nid, stepOrder);

      const stepType = node.data.agentUuid ? "agent" : "prompt";
      orderedSteps.push({
        agent_uuid: node.data.agentUuid,
        step_type: stepType,
        instructions: typeof node.data.config?.prompt === "string" ? node.data.config.prompt : undefined,
        expected_output:
          typeof node.data.config?.expected_output === "string" ? node.data.config.expected_output : undefined,
        config: { ...node.data.config, label: node.data.label || undefined },
        requires_review: node.data.requiresReview,
      });

      const pid = parentMap.get(nid);
      if (pid != null) {
        connectors.push({
          from_step_order: orderMap.get(pid),
          to_step_order: stepOrder,
        });
      }

      queue.push(...(childrenOf.get(nid) || []));
    }

    onSave(orderedSteps, connectors);
  };

  const defaultViewport = useMemo(() => ({ x: 0, y: 0, zoom: 0.85 }), []);

  // ---- render ----

  return (
    <div className="flex h-[600px] border border-border-secondary rounded-lg overflow-hidden bg-surface-base">
      {/* Canvas */}
      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onNodeClick={handleNodeClick}
          nodeTypes={nodeTypes}
          fitView
          defaultViewport={defaultViewport}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={true}
          className="bg-surface-base"
          proOptions={{ hideAttribution: true }}
        >
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} color="#475569" />
          <Controls className="bg-surface-secondary border border-border-secondary [&_button]:bg-surface-secondary [&_button]:border-border-secondary [&_button]:text-content-secondary [&_button:hover]:bg-interactive-active" />

          <Panel position="top-left">
            <button
              onClick={handleAddStep}
              className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity shadow-lg"
            >
              <Plus className="h-4 w-4" />
              Add Step
            </button>
          </Panel>

          <Panel position="top-right">
            <div className="flex items-center gap-2">
              <button
                onClick={onCancel}
                className="px-4 py-2 bg-surface-secondary text-content-secondary rounded-lg hover:bg-interactive-active transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
              <button
                onClick={handleSave}
                disabled={nodes.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="h-4 w-4" />
                Save Flow
              </button>
            </div>
          </Panel>

          {nodes.length > 0 && (
            <Panel position="bottom-left">
              <div className="bg-surface-secondary/90 backdrop-blur-xs border border-border-secondary rounded-lg px-3 py-2 text-xs text-content-tertiary space-y-1">
                <div className="flex items-center gap-2">
                  <Plus className="h-3 w-3" />
                  <span>Hover a step, click + to fan out</span>
                </div>
                <div className="flex items-center gap-2">
                  <GitBranch className="h-3 w-3" />
                  <span>Sibling branches run in parallel</span>
                </div>
              </div>
            </Panel>
          )}
        </ReactFlow>

        {nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <Bot className="h-12 w-12 text-content-quaternary mx-auto mb-3" />
              <p className="text-content-quaternary text-sm">
                Click &quot;Add Step&quot; to start building your workflow
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ---- Right panel: step config OR agent picker ---- */}
      {selectedNode && (
        <div className="w-80 bg-surface-primary border-l border-border-secondary p-4 overflow-y-auto">
          {showAgentPicker ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-content-primary">Assign Agent</h3>
                <button
                  onClick={() => setShowAgentPicker(false)}
                  className="p-1 text-content-tertiary hover:text-content-primary transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {loadingAgents ? (
                <div className="text-center py-8 text-content-tertiary">Loading agents...</div>
              ) : (
                <AgentPickerList agents={agents} onAssign={handleAssignAgent} />
              )}
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-content-primary">Step Configuration</h3>
                <button
                  onClick={() => setSelectedNode(null)}
                  className="p-1 text-content-tertiary hover:text-content-primary transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Step label */}
                <div>
                  <label htmlFor="flow-step-label" className="block text-sm font-medium text-content-secondary mb-1.5">
                    Step Name
                  </label>
                  <input
                    id="flow-step-label"
                    type="text"
                    value={selectedNode.data.label}
                    onChange={(e) => handleUpdateNodeConfig({ label: e.target.value })}
                    className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary text-sm"
                  />
                </div>

                {/* Connected From */}
                <div>
                  <span className="block text-sm font-medium text-content-secondary mb-1.5">Connected From</span>
                  <select
                    value={parentMap.get(selectedNode.id) ?? "__none__"}
                    onChange={(e) => {
                      const newParentId = e.target.value === "__none__" ? null : e.target.value;
                      if (newParentId === selectedNode.id) return;
                      setParentMap((prev) => {
                        const next = new Map(prev);
                        next.set(selectedNode.id, newParentId);
                        return next;
                      });
                    }}
                    className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary text-sm focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                  >
                    <option value="__none__">None (root step)</option>
                    {nodes
                      .filter((n) => {
                        if (n.id === selectedNode.id) return false;
                        // Exclude descendants to prevent cycles
                        const descendants = new Set<string>();
                        const queue = [selectedNode.id];
                        while (queue.length > 0) {
                          const cur = queue.pop()!;
                          for (const [childId, pid] of parentMap.entries()) {
                            if (pid === cur && !descendants.has(childId)) {
                              descendants.add(childId);
                              queue.push(childId);
                            }
                          }
                        }
                        return !descendants.has(n.id);
                      })
                      .map((n) => (
                        <option key={n.id} value={n.id}>
                          {n.data.label || n.data.agentName || "Step"}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Agent (optional) */}
                <div>
                  <span className="block text-sm font-medium text-content-secondary mb-1.5">
                    Agent <span className="text-content-quaternary font-normal">(optional)</span>
                  </span>
                  {selectedNode.data.agentName ? (
                    <div className="flex items-center justify-between px-3 py-2 bg-surface-secondary rounded-lg border border-border-secondary">
                      <span className="text-sm text-content-primary">{selectedNode.data.agentName}</span>
                      <button
                        type="button"
                        onClick={handleClearAgent}
                        className="text-content-quaternary hover:text-red-400 transition-colors"
                        title="Remove agent"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowAgentPicker(true)}
                      className="w-full px-3 py-2 bg-surface-secondary border border-dashed border-border-secondary rounded-lg text-sm text-content-tertiary hover:border-brand-primary hover:text-content-secondary transition-colors text-left"
                    >
                      + Assign an agent...
                    </button>
                  )}
                </div>

                {/* Instructions */}
                <div>
                  <label
                    htmlFor="flow-node-custom-prompt"
                    className="block text-sm font-medium text-content-secondary mb-1.5"
                  >
                    Instructions
                  </label>
                  <textarea
                    id="flow-node-custom-prompt"
                    rows={4}
                    placeholder="What should this step do?"
                    value={typeof selectedNode.data.config?.prompt === "string" ? selectedNode.data.config.prompt : ""}
                    onChange={(e) =>
                      handleUpdateNodeConfig({
                        config: { ...selectedNode.data.config, prompt: e.target.value },
                      })
                    }
                    className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary resize-none text-sm"
                  />
                </div>

                {/* Expected Output toggle */}
                <div>
                  <Toggle
                    label="Expected Output"
                    checked={selectedNode.data.config?.expected_output != null}
                    onChange={(on) => {
                      handleUpdateNodeConfig({
                        config: {
                          ...selectedNode.data.config,
                          expected_output: on ? "" : undefined,
                        },
                      });
                    }}
                  />
                  {selectedNode.data.config?.expected_output != null && (
                    <textarea
                      rows={3}
                      placeholder="Describe the expected output format..."
                      value={
                        typeof selectedNode.data.config?.expected_output === "string"
                          ? selectedNode.data.config.expected_output
                          : ""
                      }
                      onChange={(e) =>
                        handleUpdateNodeConfig({
                          config: {
                            ...selectedNode.data.config,
                            expected_output: e.target.value,
                          },
                        })
                      }
                      className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary resize-none text-sm"
                    />
                  )}
                </div>

                {/* Review toggle */}
                <Toggle
                  label="Requires Human Review"
                  checked={selectedNode.data.requiresReview}
                  onChange={(on) => handleUpdateNodeConfig({ requiresReview: on })}
                />

                {selectedNode.data.requiresReview && (
                  <div className="text-xs text-content-tertiary bg-surface-secondary/50 border border-border-secondary rounded-lg p-3">
                    <Clock className="h-4 w-4 text-yellow-400 inline mr-1" />
                    Pauses the flow for human review before continuing.
                  </div>
                )}

                {/* Delete */}
                <div className="pt-2 border-t border-border-secondary">
                  <button
                    onClick={() => handleDeleteNode(selectedNode.id)}
                    className="flex items-center gap-2 px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors text-sm w-full"
                  >
                    <Trash2 className="h-4 w-4" />
                    Remove this step
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
